import { Divider } from '@mui/material';
import SliderFormBody from '../common/SliderFormBody';
import SliderFormField from '../common/SliderFormField';
import SliderFormSection from '../common/SliderFormSection';
import SliderFormText from '../common/SliderFormText';
import { Checkbox, MuiSelect, MuiTextInput } from '@hometap/htco-components';
import cx from 'classnames';
import { emailValidator, phoneValidator } from 'utils/validators';
import Callout from 'components/Callout/Callout';
import type { FormErrors, RegisterField } from 'apps/types/genericTypes';
import type { AppraisalOrderFormValues, AppraisalOrderPrefilledOrderValues } from '../types/types';
import type { AppraisalProductNode } from 'apps/track-details/ApplicationReview/sections/HomeValuationsController/types/reggoraTypes';
import './AppraisalsFormsStyle.scss';
import { ReactNode } from 'react';

export type AppraisalOrderFormProps = {
  loading?: boolean;
  isFollowUp: boolean;
  errorMessage?: ReactNode;
  isInSlider?: boolean;
  prefilledOrderValues: AppraisalOrderPrefilledOrderValues;
  productOptions: AppraisalProductNode[];
  formErrors: FormErrors<AppraisalOrderFormValues>;
  registerField: RegisterField;
  productOnBlur: () => void;
  formData: AppraisalOrderFormValues;
  updateFormData?: (newValues: Partial<AppraisalOrderFormValues>) => void;
};

const AppraisalOrderForm = ({
  loading = false,
  isFollowUp,
  errorMessage,
  isInSlider = false,
  prefilledOrderValues,
  productOptions,
  formErrors,
  registerField,
  productOnBlur,
  formData,
  updateFormData = () => {},
}: AppraisalOrderFormProps) => {
  return (
    <SliderFormBody>
      <SliderFormSection heading={'INFO'}>
        <SliderFormField label={'Order ID'} field={<SliderFormText text={prefilledOrderValues?.friendlyId} />} />
        <SliderFormField
          label={'Address'}
          field={
            <div className="flex flex-col">
              <SliderFormText text={prefilledOrderValues?.addressLine1} />
              <SliderFormText text={prefilledOrderValues?.addressLine2} />
            </div>
          }
        />
        <SliderFormField label={'Property Type'} field={<SliderFormText text={prefilledOrderValues?.propertyType} />} />
      </SliderFormSection>
      <Divider />
      <SliderFormSection heading={'APPRAISAL'}>
        <SliderFormField
          label={'Appraisal Type'}
          field={<SliderFormText text={prefilledOrderValues?.appraisalType} />}
        />
        <SliderFormField
          label={'Product'}
          field={
            <MuiSelect
              aria-label="Product"
              required
              showRequiredAsterisk={false}
              options={productOptions.map(product => ({ label: product.name, value: product.id }))}
              {...registerField('products')}
              width={'100%'}
              theme="outlined"
              classNamePrefix="SliderFormSelect"
              disabled={loading}
              onBlur={productOnBlur}
            />
          }
        />
      </SliderFormSection>
      <Divider />
      <SliderFormSection heading={'CONTACT'}>
        {errorMessage ? (
          <>
            <SliderFormField label={'First name'} field={<SliderFormText text={formData?.firstName} />} />
            <SliderFormField label={'Last name'} field={<SliderFormText text={formData?.lastName} />} />
            <SliderFormField label={'Email address'} field={<SliderFormText text={formData?.emailAddress} />} />
            <SliderFormField label={'Phone Number'} field={<SliderFormText text={formData?.phoneNumber} />} />
          </>
        ) : (
          <>
            <SliderFormField
              label={'First name'}
              field={
                <MuiTextInput
                  required
                  theme="outlined"
                  {...registerField('firstName')}
                  disabled={loading || isFollowUp}
                  error={formErrors?.firstName?.message}
                  aria-label="First name"
                />
              }
              className={cx('SliderFormTextInputField', { 'mb-[32px]': formErrors?.firstName })}
            />
            <SliderFormField
              label={'Last name'}
              field={
                <MuiTextInput
                  required
                  theme="outlined"
                  {...registerField('lastName')}
                  disabled={loading || isFollowUp}
                  error={formErrors?.lastName?.message}
                  aria-label="Last name"
                />
              }
              className={cx('SliderFormTextInputField', { 'mb-[32px]': formErrors?.lastName })}
            />
            <SliderFormField
              label={'Email address'}
              field={
                <MuiTextInput
                  required
                  theme="outlined"
                  validator={emailValidator}
                  {...registerField('emailAddress')}
                  disabled={loading || isFollowUp}
                  error={formErrors?.emailAddress?.message}
                  aria-label="Email address"
                />
              }
              className={cx('SliderFormTextInputField', { 'mb-[32px]': formErrors?.emailAddress })}
            />
            <SliderFormField
              label={'Phone Number'}
              field={
                <MuiTextInput
                  required
                  theme="outlined"
                  validator={phoneValidator}
                  {...registerField('phoneNumber')}
                  disabled={loading || isFollowUp}
                  error={formErrors?.phoneNumber?.message}
                  aria-label="Phone number"
                />
              }
              className={cx('SliderFormTextInputField', { 'mb-[32px]': formErrors?.phoneNumber })}
            />
          </>
        )}
      </SliderFormSection>
      {errorMessage && <Callout type="error">{errorMessage}</Callout>}
      {!isInSlider && prefilledOrderValues && (
        <Checkbox
          disabled={loading}
          label={<strong className="text-sm">Rush order</strong>}
          checked={formData?.rush}
          onChange={(value: boolean) => updateFormData({ rush: value })}
        />
      )}
    </SliderFormBody>
  );
};

export default AppraisalOrderForm;
